import React, { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { spring } from '../../utils/motion';
type Side = 'right' | 'bottom' | 'center';
export function Sheet({
  open,
  onClose,
  title,
  description,
  side = 'right',
  width = 'max-w-md',
  children,
  footer
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  side?: Side;
  width?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const panelMotion = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.15
    }
  } : side === 'right' ? {
    initial: {
      x: 40,
      opacity: 0
    },
    animate: {
      x: 0,
      opacity: 1
    },
    exit: {
      x: 40,
      opacity: 0
    },
    transition: spring.smooth
  } : side === 'bottom' ? {
    initial: {
      y: 40,
      opacity: 0
    },
    animate: {
      y: 0,
      opacity: 1
    },
    exit: {
      y: 40,
      opacity: 0
    },
    transition: spring.smooth
  } : {
    initial: {
      scale: 0.96,
      opacity: 0
    },
    animate: {
      scale: 1,
      opacity: 1
    },
    exit: {
      scale: 0.98,
      opacity: 0
    },
    transition: spring.smooth
  };
  return <AnimatePresence data-id="element-984" data-mp="5lbbhe2ur9k5">
      {open && <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={title} data-id="element-985" data-mp="5m4blw7b30tb">
          <motion.button type="button" aria-label="Close" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]" data-id="element-986" data-mp="aj4ksfzgcjon" />
          <motion.div {...panelMotion} className={cn('relative z-10 flex w-full flex-col bg-canvas shadow-float', side === 'right' && cn('ml-auto h-full border-l border-hairline', width), side === 'bottom' && 'mt-auto max-h-[88vh] rounded-t-2xl border-t border-hairline', side === 'center' && cn('m-auto max-h-[86vh] rounded-2xl border border-hairline', width))} data-id="element-987" data-mp="q5hbu7915cze">
            <header className="flex items-start gap-4 border-b border-hairline px-5 py-4" data-id="element-988" data-mp="281skmna76oz">
              <div className="min-w-0 flex-1" data-id="element-989" data-mp="2aelfuukv1gt">
                <h2 className="font-display text-lg font-semibold leading-tight text-ink" data-id="element-990" data-mp="g8w258m2fdp8">{title}</h2>
                {description && <p className="mt-1 text-[13px] text-ink-2" data-id="element-991" data-mp="b76qpruxky0m">{description}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-ink-3 transition-colors duration-150 ease-out hover:bg-oat hover:text-ink" data-id="element-992" data-mp="1rlovuoyf2c7x">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-993" data-mp="1z70qkox4rfvd" />
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" data-id="element-994" data-mp="1l79kdacuv3rx">{children}</div>
            {footer && <footer className="border-t border-hairline bg-surface px-5 py-3" data-id="element-995" data-mp="1q5azjk75uiy4">{footer}</footer>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}